import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { parseDateOnly } from '@/utils/dateUtils';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import Page from '@/components/page/Page';
import { Card } from '@/components/ui/card';
import { DataTable } from '@/components/common/data-table/data-table';
import { DataTablePagination } from '@/components/common/data-table/data-table-pagination';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  CalendarIcon,
  XIcon,
  PlusIcon,
  PaperclipIcon,
  CircleNotchIcon,
  TrashIcon,
} from '@phosphor-icons/react';

import {
  useDiscountsList,
  useMultiCustomerListPrices,
  useDeleteDiscount,
  type ListPricesMap,
  type CustomerSkusInput,
} from '@/api/rpc/hooks/accounts-receivable/useDiscounts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AttachmentViewerModal } from '@/components/common/AttachmentViewerModal';
import { CreateDiscountFormModal } from './CreateDiscountFormModal';
import type { Discount, DiscountType } from '@erp/rpc';

type DiscountRow = Discount;

// Tab types for filtering by scope
type ScopeTab = 'sku' | 'category' | 'line' | 'group' | 'all_products';

const SCOPE_TABS: { value: ScopeTab; label: string; targetType: string }[] = [
  { value: 'sku', label: 'SKU', targetType: 'SKU' },
  { value: 'category', label: 'Category', targetType: 'PRODUCT_CATEGORY' },
  { value: 'line', label: 'Line', targetType: 'PRODUCT_LINE' },
  { value: 'group', label: 'Group', targetType: 'PRODUCT_DISCOUNT_GROUP' },
  { value: 'all_products', label: 'All Products', targetType: 'ALL_PRODUCTS' },
];

// Filter visibility configuration per tab
const TAB_FILTERS: Record<
  ScopeTab,
  { sku: boolean; customer: boolean; category: boolean; productLine: boolean }
> = {
  sku: { sku: true, customer: true, category: true, productLine: true },
  category: { sku: false, customer: true, category: true, productLine: false },
  line: { sku: false, customer: true, category: false, productLine: true },
  group: { sku: false, customer: true, category: false, productLine: false },
  all_products: {
    sku: false,
    customer: true,
    category: false,
    productLine: false,
  },
};

function makeListPriceKey(customerId: string, sku: string): string {
  return `${customerId}:${sku}`;
}

/**
 * Format the discount value based on type
 */
function formatDiscountValue(discount: DiscountRow): string {
  switch (discount.discountType) {
    case 'PERCENT':
      if (discount.percentBps !== null) {
        const percent = discount.percentBps / 100;
        return `${percent.toFixed(2)}%`;
      }
      return '—';
    case 'FIXED_PRICE':
      if (discount.value !== null) {
        const value = Number(discount.value);
        return value.toLocaleString(undefined, {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        });
      }
      return '—';
    default:
      return '—';
  }
}

/**
 * Format effective date range
 */
function formatEffectivePeriod(discount: DiscountRow): string {
  const startDate = discount.effectiveStart
    ? parseDateOnly(discount.effectiveStart)
    : null;
  const endDate = discount.effectiveEnd
    ? parseDateOnly(discount.effectiveEnd)
    : null;

  const start = startDate ? format(startDate, 'MM/dd/yyyy') : 'No start';
  const end = endDate ? format(endDate, 'MM/dd/yyyy') : 'No end';

  if (!discount.effectiveStart && !discount.effectiveEnd) {
    return 'Always active';
  }

  return `${start} - ${end}`;
}

/**
 * Check if discount is currently effective
 */
function isCurrentlyEffective(discount: DiscountRow): boolean {
  if (!discount.isActive) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (discount.effectiveStart) {
    const start = parseDateOnly(discount.effectiveStart);
    if (start && start > today) return false;
  }

  if (discount.effectiveEnd) {
    const end = parseDateOnly(discount.effectiveEnd);
    if (end && end < today) return false;
  }

  return true;
}

/**
 * Cell component for displaying list price
 */
function ListPriceCell({
  scopes,
  listPrices,
  isLoading,
}: {
  scopes: DiscountRow['scopes'];
  listPrices: ListPricesMap;
  isLoading: boolean;
}) {
  // Find first scope with both customer and product
  const firstScope = scopes?.find((s) => s.customer?.id && s.product?.sku);

  if (!firstScope) {
    return <span className="text-muted-foreground text-sm">-</span>;
  }

  const customerId = firstScope.customer?.id;
  const sku = firstScope.product?.sku;

  if (!customerId || !sku) {
    return <span className="text-muted-foreground text-sm">-</span>;
  }

  if (isLoading) {
    return <CircleNotchIcon size={14} className="text-blue-500 animate-spin" />;
  }

  const key = makeListPriceKey(customerId, sku);
  const priceData = listPrices.get(key);

  if (!priceData) {
    return <span className="text-muted-foreground text-sm">-</span>;
  }

  const formattedPrice = priceData.listPrice.toLocaleString(undefined, {
    style: 'currency',
    currency: priceData.currencyCode ?? 'USD',
    minimumFractionDigits: 2,
  });

  return (
    <span className="text-sm text-muted-foreground">{formattedPrice}</span>
  );
}

/**
 * Helper to get list price data for a discount's first scope
 */
function getListPriceForDiscount(
  scopes: DiscountRow['scopes'],
  listPrices: ListPricesMap,
): { listPrice: number; currencyCode: string | null } | null {
  const firstScope = scopes?.find((s) => s.customer?.id && s.product?.sku);
  if (!firstScope) return null;

  const customerId = firstScope.customer?.id;
  const sku = firstScope.product?.sku;
  if (!customerId || !sku) return null;

  const key = makeListPriceKey(customerId, sku);
  return listPrices.get(key) ?? null;
}

/**
 * Cell component for displaying discounted price (always shows $ amount)
 * - FIXED_PRICE: display value directly
 * - PERCENT: calculate listPrice * (1 - percentBps/10000)
 */
function DiscountedPriceCell({
  discount,
  listPrices,
  isLoading,
}: {
  discount: DiscountRow;
  listPrices: ListPricesMap;
  isLoading: boolean;
}) {
  // For FIXED_PRICE, we always have the value
  if (discount.discountType === 'FIXED_PRICE') {
    if (discount.value !== null) {
      const value = Number(discount.value);
      const formatted = value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      });
      return (
        <span className="text-sm font-medium text-foreground">{formatted}</span>
      );
    }
    return <span className="text-muted-foreground text-sm">-</span>;
  }

  // For PERCENT, we need to calculate from list price
  if (discount.discountType === 'PERCENT') {
    if (isLoading) {
      return (
        <CircleNotchIcon size={14} className="text-blue-500 animate-spin" />
      );
    }

    const priceData = getListPriceForDiscount(discount.scopes, listPrices);
    if (!priceData || discount.percentBps === null) {
      return <span className="text-muted-foreground text-sm">-</span>;
    }

    // Calculate discounted price: listPrice * (1 - percent/100)
    const percentDecimal = discount.percentBps / 10000; // Convert basis points to decimal
    const discountedPrice = priceData.listPrice * (1 - percentDecimal);

    const formatted = discountedPrice.toLocaleString(undefined, {
      style: 'currency',
      currency: priceData.currencyCode ?? 'USD',
      minimumFractionDigits: 2,
    });
    return (
      <span className="text-sm font-medium text-foreground">{formatted}</span>
    );
  }

  return <span className="text-muted-foreground text-sm">-</span>;
}

/**
 * Cell component for displaying discount percentage
 * - PERCENT: display percentBps / 100 directly
 * - FIXED_PRICE: calculate (listPrice - value) / listPrice * 100
 */
function DiscountPercentCell({
  discount,
  listPrices,
  isLoading,
}: {
  discount: DiscountRow;
  listPrices: ListPricesMap;
  isLoading: boolean;
}) {
  // For PERCENT, we always have the percentage
  if (discount.discountType === 'PERCENT') {
    if (discount.percentBps !== null) {
      const percent = discount.percentBps / 100;
      return (
        <span className="text-sm text-foreground">{percent.toFixed(2)}%</span>
      );
    }
    return <span className="text-muted-foreground text-sm">-</span>;
  }

  // For FIXED_PRICE, we need to calculate from list price
  if (discount.discountType === 'FIXED_PRICE') {
    if (isLoading) {
      return (
        <CircleNotchIcon size={14} className="text-blue-500 animate-spin" />
      );
    }

    const priceData = getListPriceForDiscount(discount.scopes, listPrices);
    if (!priceData || discount.value === null) {
      return <span className="text-muted-foreground text-sm">-</span>;
    }

    const value = Number(discount.value);
    if (priceData.listPrice === 0) {
      return <span className="text-muted-foreground text-sm">-</span>;
    }

    // Calculate discount percentage: (listPrice - value) / listPrice * 100
    const discountPercent =
      ((priceData.listPrice - value) / priceData.listPrice) * 100;

    return (
      <span className="text-sm text-foreground">
        {discountPercent.toFixed(2)}%
      </span>
    );
  }

  return <span className="text-muted-foreground text-sm">-</span>;
}

/**
 * Cell component for displaying discount attachments
 * Manages its own modal state for viewing the attachment
 */
function AttachmentCell({ scopes }: { scopes: DiscountRow['scopes'] }) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Find the first scope with an attachment
  const attachmentUrl = scopes?.find((s) => s.attachment)?.attachment;

  if (!attachmentUrl) {
    return <span className="text-muted-foreground text-sm">-</span>;
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={() => setIsModalOpen(true)}
      >
        <PaperclipIcon className="h-4 w-4 text-primary" />
      </Button>
      <AttachmentViewerModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        attachmentUrl={attachmentUrl}
        title="Discount Attachment"
      />
    </>
  );
}

/**
 * Creates column definitions for the SKU tab.
 * Shows all columns including SKU, Product Category, Product Line, List Price, etc.
 */
function createSkuColumns(
  listPrices: ListPricesMap,
  isListPricesLoading: boolean,
  onDeleteClick: (discount: DiscountRow) => void,
): ColumnDef<DiscountRow>[] {
  return [
    {
      id: 'customer',
      header: 'Customer',
      cell: ({ row }) => {
        const scopes = row.original.scopes ?? [];
        const customers = scopes
          .map((s) => s.customer?.name)
          .filter(Boolean) as string[];

        if (customers.length === 0) {
          return (
            <span className="text-muted-foreground text-sm">All Customers</span>
          );
        }
        if (customers.length === 1) {
          return (
            <span className="text-sm text-foreground">{customers[0]}</span>
          );
        }

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-sm text-foreground">
                {customers[0]}{' '}
                <span className="text-muted-foreground">
                  +{customers.length - 1} more
                </span>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{customers.join(', ')}</p>
            </TooltipContent>
          </Tooltip>
        );
      },
      size: 180,
    },
    {
      id: 'sku',
      header: 'SKU',
      cell: ({ row }) => {
        const scopes = row.original.scopes ?? [];

        // Check if this is a non-SKU target type
        const nonSkuScope = scopes.find(
          (s) =>
            s.targetType === 'PRODUCT_CATEGORY' ||
            s.targetType === 'PRODUCT_LINE' ||
            s.targetType === 'PRODUCT_DISCOUNT_GROUP' ||
            s.targetType === 'ALL_PRODUCTS',
        );

        if (nonSkuScope) {
          // Show appropriate label based on target type
          if (nonSkuScope.targetType === 'PRODUCT_CATEGORY') {
            return (
              <span className="text-muted-foreground text-sm italic">
                Category: {nonSkuScope.targetValue}
              </span>
            );
          }
          if (nonSkuScope.targetType === 'PRODUCT_LINE') {
            return (
              <span className="text-muted-foreground text-sm italic">
                Line: {nonSkuScope.targetValue}
              </span>
            );
          }
          if (nonSkuScope.targetType === 'PRODUCT_DISCOUNT_GROUP') {
            return (
              <span className="text-muted-foreground text-sm italic">
                Group: {nonSkuScope.productDiscountGroup?.name ?? '-'}
              </span>
            );
          }
          if (nonSkuScope.targetType === 'ALL_PRODUCTS') {
            return (
              <span className="text-muted-foreground text-sm">
                All Products
              </span>
            );
          }
        }

        // SKU target type - show actual SKUs
        const skus = scopes
          .map((s) => s.product?.sku)
          .filter(Boolean) as string[];

        if (skus.length === 0) {
          return <span className="text-muted-foreground text-sm">-</span>;
        }
        if (skus.length === 1) {
          return (
            <span className="text-sm font-mono text-foreground">{skus[0]}</span>
          );
        }

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-sm font-mono text-foreground">
                {skus[0]}{' '}
                <span className="text-muted-foreground font-sans">
                  +{skus.length - 1} more
                </span>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-mono">{skus.join(', ')}</p>
            </TooltipContent>
          </Tooltip>
        );
      },
      size: 140,
    },
    {
      id: 'productCategory',
      header: 'Product Category',
      cell: ({ row }) => {
        const scopes = row.original.scopes ?? [];
        const categories = new Set<string>();
        scopes.forEach((s) => {
          // For PRODUCT_CATEGORY target type, use targetValue
          if (
            s.targetType === 'PRODUCT_CATEGORY' &&
            s.targetValue &&
            s.targetValue.toUpperCase() !== 'UNASSIGNED'
          ) {
            categories.add(s.targetValue);
          }
          // For SKU target type, extract from product tags
          const tags = s.product?.tags ?? [];
          if (tags[0] && tags[0].toUpperCase() !== 'UNASSIGNED') {
            categories.add(tags[0]);
          }
        });

        if (categories.size === 0) {
          return <span className="text-muted-foreground text-sm">-</span>;
        }

        const categoryList = Array.from(categories);
        return (
          <span className="text-sm text-muted-foreground">
            {categoryList.join(', ')}
          </span>
        );
      },
      size: 140,
    },
    {
      id: 'productLine',
      header: 'Product Line',
      cell: ({ row }) => {
        const scopes = row.original.scopes ?? [];
        const productLines = new Set<string>();
        scopes.forEach((s) => {
          // For PRODUCT_LINE target type, use targetValue
          if (
            s.targetType === 'PRODUCT_LINE' &&
            s.targetValue &&
            s.targetValue.toUpperCase() !== 'UNASSIGNED'
          ) {
            productLines.add(s.targetValue);
          }
          // For SKU target type, extract from product tags
          const tags = s.product?.tags ?? [];
          if (tags[1] && tags[1].toUpperCase() !== 'UNASSIGNED') {
            productLines.add(tags[1]);
          }
        });

        if (productLines.size === 0) {
          return <span className="text-muted-foreground text-sm">-</span>;
        }

        const lineList = Array.from(productLines);
        return (
          <span className="text-sm text-muted-foreground">
            {lineList.join(', ')}
          </span>
        );
      },
      size: 130,
    },
    {
      id: 'discountedPrice',
      header: 'Discounted Price',
      cell: ({ row }) => (
        <DiscountedPriceCell
          discount={row.original}
          listPrices={listPrices}
          isLoading={isListPricesLoading}
        />
      ),
      size: 140,
    },
    {
      id: 'discountPercent',
      header: 'Discount %',
      cell: ({ row }) => (
        <DiscountPercentCell
          discount={row.original}
          listPrices={listPrices}
          isLoading={isListPricesLoading}
        />
      ),
      size: 100,
    },
    {
      id: 'listPrice',
      header: 'List Price',
      cell: ({ row }) => (
        <ListPriceCell
          scopes={row.original.scopes}
          listPrices={listPrices}
          isLoading={isListPricesLoading}
        />
      ),
      size: 120,
    },
    {
      accessorKey: 'discountType',
      header: 'Type',
      cell: ({ getValue }) => {
        const type = getValue<DiscountType>();
        // Convert "FIXED_PRICE" to "Fixed price"
        const label = type
          .toLowerCase()
          .replace('_', ' ')
          .replace(/^\w/, (c) => c.toUpperCase());
        return <span className="text-sm text-muted-foreground">{label}</span>;
      },
      size: 100,
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ getValue, row }) => {
        const isActive = getValue<boolean>();
        const isEffective = isCurrentlyEffective(row.original);

        if (!isActive) {
          return (
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-gray-400" />
              <span className="text-sm text-muted-foreground">Inactive</span>
            </div>
          );
        }

        if (isEffective) {
          return (
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
              <span className="text-sm text-muted-foreground">Active</span>
            </div>
          );
        }

        return (
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
            <span className="text-sm text-muted-foreground">Scheduled</span>
          </div>
        );
      },
      size: 120,
    },
    {
      id: 'effectivePeriod',
      header: 'Effective Period',
      cell: ({ row }) => {
        const discount = row.original;
        const isEffective = isCurrentlyEffective(discount);

        return (
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {formatEffectivePeriod(discount)}
            </span>
            {discount.isActive && !isEffective && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-xs">
                    Scheduled
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    This discount is not currently within its effective period
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      },
      size: 220,
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: ({ row }) => {
        const createdBy = row.original.createdBy;

        if (!createdBy) {
          return <span className="text-sm text-muted-foreground">System</span>;
        }

        return (
          <span className="text-sm text-foreground">
            {createdBy.name || createdBy.email || 'System'}
          </span>
        );
      },
      size: 160,
    },
    {
      id: 'endCustomer',
      header: 'End Customer',
      cell: ({ row }) => {
        const scopes = row.original.scopes ?? [];
        const endCustomerName = scopes.find(
          (s) => s.endCustomerName,
        )?.endCustomerName;

        if (!endCustomerName) {
          return <span className="text-muted-foreground text-sm">-</span>;
        }

        return (
          <span className="text-sm text-foreground">{endCustomerName}</span>
        );
      },
      size: 150,
    },
    {
      id: 'attachment',
      header: 'Attachment',
      cell: ({ row }) => <AttachmentCell scopes={row.original.scopes} />,
      size: 100,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-red-600 hover:bg-red-50"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(row.original);
              }}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Delete discount</p>
          </TooltipContent>
        </Tooltip>
      ),
      size: 50,
    },
  ];
}

/**
 * Shared column definitions that are reused across tabs
 */
function createCustomerColumn(): ColumnDef<DiscountRow> {
  return {
    id: 'customer',
    header: 'Customer',
    cell: ({ row }) => {
      const scopes = row.original.scopes ?? [];
      const customers = scopes
        .map((s) => s.customer?.name)
        .filter(Boolean) as string[];

      if (customers.length === 0) {
        return (
          <span className="text-muted-foreground text-sm">All Customers</span>
        );
      }
      if (customers.length === 1) {
        return <span className="text-sm text-foreground">{customers[0]}</span>;
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-sm text-foreground">
              {customers[0]}{' '}
              <span className="text-muted-foreground">
                +{customers.length - 1} more
              </span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{customers.join(', ')}</p>
          </TooltipContent>
        </Tooltip>
      );
    },
    size: 180,
  };
}

function createEndCustomerColumn(): ColumnDef<DiscountRow> {
  return {
    id: 'endCustomer',
    header: 'End Customer',
    cell: ({ row }) => {
      const scopes = row.original.scopes ?? [];
      const endCustomerName = scopes.find(
        (s) => s.endCustomerName,
      )?.endCustomerName;

      if (!endCustomerName) {
        return <span className="text-muted-foreground text-sm">-</span>;
      }

      return <span className="text-sm text-foreground">{endCustomerName}</span>;
    },
    size: 150,
  };
}

function createDiscountValueColumn(): ColumnDef<DiscountRow> {
  return {
    id: 'discountValue',
    header: 'Discount Value',
    cell: ({ row }) => {
      const discount = row.original;
      return (
        <span className="text-sm font-medium text-foreground">
          {formatDiscountValue(discount)}
        </span>
      );
    },
    size: 140,
  };
}

function createTypeColumn(): ColumnDef<DiscountRow> {
  return {
    accessorKey: 'discountType',
    header: 'Type',
    cell: ({ getValue }) => {
      const type = getValue<DiscountType>();
      const label = type
        .toLowerCase()
        .replace('_', ' ')
        .replace(/^\w/, (c) => c.toUpperCase());
      return <span className="text-sm text-muted-foreground">{label}</span>;
    },
    size: 100,
  };
}

function createStatusColumn(): ColumnDef<DiscountRow> {
  return {
    accessorKey: 'isActive',
    header: 'Status',
    cell: ({ getValue, row }) => {
      const isActive = getValue<boolean>();
      const isEffective = isCurrentlyEffective(row.original);

      if (!isActive) {
        return (
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-gray-400" />
            <span className="text-sm text-muted-foreground">Inactive</span>
          </div>
        );
      }

      if (isEffective) {
        return (
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
            <span className="text-sm text-muted-foreground">Active</span>
          </div>
        );
      }

      return (
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
          <span className="text-sm text-muted-foreground">Scheduled</span>
        </div>
      );
    },
    size: 120,
  };
}

function createEffectivePeriodColumn(): ColumnDef<DiscountRow> {
  return {
    id: 'effectivePeriod',
    header: 'Effective Period',
    cell: ({ row }) => {
      const discount = row.original;
      const isEffective = isCurrentlyEffective(discount);

      return (
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {formatEffectivePeriod(discount)}
          </span>
          {discount.isActive && !isEffective && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="text-xs">
                  Scheduled
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  This discount is not currently within its effective period
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
    size: 220,
  };
}

function createCreatedByColumn(): ColumnDef<DiscountRow> {
  return {
    id: 'createdBy',
    header: 'Created By',
    cell: ({ row }) => {
      const createdBy = row.original.createdBy;

      if (!createdBy) {
        return <span className="text-sm text-muted-foreground">System</span>;
      }

      return (
        <span className="text-sm text-foreground">
          {createdBy.name || createdBy.email || 'System'}
        </span>
      );
    },
    size: 160,
  };
}

function createAttachmentColumn(): ColumnDef<DiscountRow> {
  return {
    id: 'attachment',
    header: 'Attachment',
    cell: ({ row }) => <AttachmentCell scopes={row.original.scopes} />,
    size: 100,
  };
}

function createActionsColumn(
  onDeleteClick: (discount: DiscountRow) => void,
): ColumnDef<DiscountRow> {
  return {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-red-600 hover:bg-red-50"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick(row.original);
            }}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Delete discount</p>
        </TooltipContent>
      </Tooltip>
    ),
    size: 50,
  };
}

/**
 * Creates column definitions for the Category tab.
 * Shows: Customer, Category (target), Discount Value, Type, Status, Effective Period, Created By, Attachment, Actions
 */
function createCategoryColumns(
  onDeleteClick: (discount: DiscountRow) => void,
): ColumnDef<DiscountRow>[] {
  return [
    createCustomerColumn(),
    {
      id: 'category',
      header: 'Category',
      cell: ({ row }) => {
        const scopes = row.original.scopes ?? [];
        const categoryScope = scopes.find(
          (s) => s.targetType === 'PRODUCT_CATEGORY',
        );
        return (
          <span className="text-sm font-medium text-foreground">
            {categoryScope?.targetValue ?? '-'}
          </span>
        );
      },
      size: 150,
    },
    createDiscountValueColumn(),
    createTypeColumn(),
    createStatusColumn(),
    createEffectivePeriodColumn(),
    createCreatedByColumn(),
    createEndCustomerColumn(),
    createAttachmentColumn(),
    createActionsColumn(onDeleteClick),
  ];
}

/**
 * Creates column definitions for the Line tab.
 * Shows: Customer, Product Line (target), Discount Value, Type, Status, Effective Period, Created By, Attachment, Actions
 */
function createLineColumns(
  onDeleteClick: (discount: DiscountRow) => void,
): ColumnDef<DiscountRow>[] {
  return [
    createCustomerColumn(),
    {
      id: 'productLine',
      header: 'Product Line',
      cell: ({ row }) => {
        const scopes = row.original.scopes ?? [];
        const lineScope = scopes.find((s) => s.targetType === 'PRODUCT_LINE');
        return (
          <span className="text-sm font-medium text-foreground">
            {lineScope?.targetValue ?? '-'}
          </span>
        );
      },
      size: 150,
    },
    createDiscountValueColumn(),
    createTypeColumn(),
    createStatusColumn(),
    createEffectivePeriodColumn(),
    createCreatedByColumn(),
    createEndCustomerColumn(),
    createAttachmentColumn(),
    createActionsColumn(onDeleteClick),
  ];
}

/**
 * Creates column definitions for the Group tab.
 * Shows: Customer, Discount Group, Discount Value, Type, Status, Effective Period, Created By, Attachment, Actions
 */
function createGroupColumns(
  onDeleteClick: (discount: DiscountRow) => void,
): ColumnDef<DiscountRow>[] {
  return [
    createCustomerColumn(),
    {
      id: 'discountGroup',
      header: 'Discount Group',
      cell: ({ row }) => {
        const scopes = row.original.scopes ?? [];
        const groupScope = scopes.find(
          (s) => s.targetType === 'PRODUCT_DISCOUNT_GROUP',
        );
        return (
          <span className="text-sm font-medium text-foreground">
            {groupScope?.productDiscountGroup?.name ?? '-'}
          </span>
        );
      },
      size: 180,
    },
    createDiscountValueColumn(),
    createTypeColumn(),
    createStatusColumn(),
    createEffectivePeriodColumn(),
    createCreatedByColumn(),
    createEndCustomerColumn(),
    createAttachmentColumn(),
    createActionsColumn(onDeleteClick),
  ];
}

/**
 * Creates column definitions for the All Products tab.
 * Shows: Customer, Discount Value, Type, Status, Effective Period, Created By, Attachment, Actions
 */
function createAllProductsColumns(
  onDeleteClick: (discount: DiscountRow) => void,
): ColumnDef<DiscountRow>[] {
  return [
    createCustomerColumn(),
    createDiscountValueColumn(),
    createTypeColumn(),
    createStatusColumn(),
    createEffectivePeriodColumn(),
    createCreatedByColumn(),
    createEndCustomerColumn(),
    createAttachmentColumn(),
    createActionsColumn(onDeleteClick),
  ];
}

export function DiscountsPage() {
  // Read URL search params for pre-filtering
  const searchParams = useSearch({ strict: false }) as {
    sku?: string;
    customerId?: string;
    customerName?: string;
    tab?: ScopeTab;
  };
  const navigate = useNavigate();
  const hasInitializedFromUrl = useRef(false);

  // Tab state from URL (defaults to 'sku')
  const selectedTab: ScopeTab = searchParams.tab ?? 'sku';

  const handleTabChange = useCallback(
    (newTab: string) => {
      const typedTab = newTab as ScopeTab;
      const newFilters = TAB_FILTERS[typedTab];

      // Clear filters that don't apply to the new tab
      if (!newFilters.sku) {
        setSelectedSkus([]);
      }
      if (!newFilters.customer) {
        setSelectedCustomers([]);
      }
      if (!newFilters.category) {
        setSelectedCategories([]);
      }
      if (!newFilters.productLine) {
        setSelectedProductLines([]);
      }

      void navigate({
        to: '.',
        search: {
          ...searchParams,
          tab: typedTab,
        },
      });
    },
    [navigate, searchParams],
  );

  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
  const [skuSearchOpen, setSkuSearchOpen] = useState(false);
  const [skuSearchValue, setSkuSearchValue] = useState('');

  const [selectedCustomers, setSelectedCustomers] = useState<
    { id: string; name: string }[]
  >([]);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchValue, setCustomerSearchValue] = useState('');

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categorySearchOpen, setCategorySearchOpen] = useState(false);
  const [categorySearchValue, setCategorySearchValue] = useState('');

  const [selectedProductLines, setSelectedProductLines] = useState<string[]>(
    [],
  );
  const [productLineSearchOpen, setProductLineSearchOpen] = useState(false);
  const [productLineSearchValue, setProductLineSearchValue] = useState('');

  // Visible filters based on selected tab
  const visibleFilters = TAB_FILTERS[selectedTab];

  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  // Create discount modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Delete discount state
  const [discountToDelete, setDiscountToDelete] = useState<DiscountRow | null>(
    null,
  );
  const deleteDiscount = useDeleteDiscount();

  // Initialize filters from URL search params on mount
  useEffect(() => {
    if (hasInitializedFromUrl.current) return;
    hasInitializedFromUrl.current = true;

    // Initialize SKU filter from URL
    if (searchParams.sku) {
      setSelectedSkus([searchParams.sku]);
    }

    // Initialize customer filter from URL
    if (searchParams.customerId && searchParams.customerName) {
      setSelectedCustomers([
        { id: searchParams.customerId, name: searchParams.customerName },
      ]);
    }
  }, [searchParams]);

  // Fetch all discounts for client-side filtering and pagination
  const queryFilters = useMemo(() => {
    return {
      limit: 1000, // Fetch all discounts
      offset: 0,
    };
  }, []);

  const { discounts, isPendingOrLoading } = useDiscountsList(queryFilters);

  // Extract unique SKUs from all fetched discounts for autocomplete
  const availableSkus = useMemo(() => {
    const skuSet = new Set<string>();
    discounts.forEach((d) => {
      d.scopes?.forEach((s) => {
        if (s.product?.sku) skuSet.add(s.product.sku);
      });
    });
    return Array.from(skuSet).sort();
  }, [discounts]);

  // Extract unique customers from all fetched discounts for autocomplete
  const availableCustomers = useMemo(() => {
    const customerMap = new Map<string, string>(); // id -> name
    discounts.forEach((d) => {
      d.scopes?.forEach((s) => {
        if (s.customer?.id && s.customer?.name) {
          customerMap.set(s.customer.id, s.customer.name);
        }
      });
    });
    return Array.from(customerMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [discounts]);

  // Extract unique product categories (first tag segment) for autocomplete
  const availableCategories = useMemo(() => {
    const categorySet = new Set<string>();
    discounts.forEach((d) => {
      d.scopes?.forEach((s) => {
        // Include targetValue for PRODUCT_CATEGORY discounts
        if (s.targetType === 'PRODUCT_CATEGORY' && s.targetValue) {
          categorySet.add(s.targetValue);
        }
        // Include first tag for SKU discounts
        const tags = s.product?.tags ?? [];
        if (tags[0]) categorySet.add(tags[0]);
      });
    });
    return Array.from(categorySet).sort();
  }, [discounts]);

  // Extract unique product lines (second tag segment or PRODUCT_LINE targetValue) for autocomplete
  const availableProductLines = useMemo(() => {
    const productLineSet = new Set<string>();
    discounts.forEach((d) => {
      d.scopes?.forEach((s) => {
        // Include targetValue for PRODUCT_LINE discounts
        if (s.targetType === 'PRODUCT_LINE' && s.targetValue) {
          productLineSet.add(s.targetValue);
        }
        // Include second tag for SKU discounts
        const tags = s.product?.tags ?? [];
        if (tags[1]) productLineSet.add(tags[1]);
      });
    });
    return Array.from(productLineSet).sort();
  }, [discounts]);

  // Filter discounts client-side based on selected SKUs, customers, and tags
  const filteredDiscounts = useMemo(() => {
    let result = discounts;

    // Filter by selected SKUs
    if (selectedSkus.length > 0) {
      result = result.filter((d) =>
        d.scopes?.some(
          (s) => s.product?.sku && selectedSkus.includes(s.product.sku),
        ),
      );
    }

    // Filter by selected customers
    if (selectedCustomers.length > 0) {
      const customerIds = selectedCustomers.map((c) => c.id);
      result = result.filter((d) =>
        d.scopes?.some(
          (s) => s.customer?.id && customerIds.includes(s.customer.id),
        ),
      );
    }

    // Filter by selected product categories (first tag segment or PRODUCT_CATEGORY targetValue)
    if (selectedCategories.length > 0) {
      result = result.filter((d) =>
        d.scopes?.some((s) => {
          // Check targetValue for PRODUCT_CATEGORY discounts
          if (
            s.targetType === 'PRODUCT_CATEGORY' &&
            s.targetValue &&
            selectedCategories.includes(s.targetValue)
          ) {
            return true;
          }
          // Check product tags for SKU discounts
          const category = s.product?.tags?.[0];
          return category && selectedCategories.includes(category);
        }),
      );
    }

    // Filter by selected product lines (second tag segment or PRODUCT_LINE targetValue)
    if (selectedProductLines.length > 0) {
      result = result.filter((d) =>
        d.scopes?.some((s) => {
          // Check targetValue for PRODUCT_LINE discounts
          if (
            s.targetType === 'PRODUCT_LINE' &&
            s.targetValue &&
            selectedProductLines.includes(s.targetValue)
          ) {
            return true;
          }
          // Check product tags for SKU discounts
          const productLine = s.product?.tags?.[1];
          return productLine && selectedProductLines.includes(productLine);
        }),
      );
    }

    return result;
  }, [
    discounts,
    selectedSkus,
    selectedCustomers,
    selectedCategories,
    selectedProductLines,
  ]);

  // Filter by selected tab (scope type)
  const tabFilteredDiscounts = useMemo(() => {
    const tabConfig = SCOPE_TABS.find((t) => t.value === selectedTab);
    if (!tabConfig) return filteredDiscounts;

    return filteredDiscounts.filter((d) =>
      d.scopes?.some((s) => s.targetType === tabConfig.targetType),
    );
  }, [filteredDiscounts, selectedTab]);

  // Count discounts for each tab (for display in tab labels)
  const tabCounts = useMemo(() => {
    const counts: Record<ScopeTab, number> = {
      sku: 0,
      category: 0,
      line: 0,
      group: 0,
      all_products: 0,
    };

    for (const discount of filteredDiscounts) {
      for (const tab of SCOPE_TABS) {
        if (discount.scopes?.some((s) => s.targetType === tab.targetType)) {
          counts[tab.value]++;
          break; // Count each discount only once per tab
        }
      }
    }

    return counts;
  }, [filteredDiscounts]);

  // Calculate which discounts are on the current page (for list price fetching)
  // This uses the pagination state directly to avoid circular dependencies
  const pageDiscounts = useMemo(() => {
    const { pageIndex, pageSize } = paginationState;
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return tabFilteredDiscounts.slice(start, end);
  }, [tabFilteredDiscounts, paginationState]);

  // Group by customer to create inputs for multi-customer list price fetching
  const customerInputs: CustomerSkusInput[] = useMemo(() => {
    const customerSkusMap = new Map<string, Set<string>>();

    for (const discount of pageDiscounts) {
      for (const scope of discount.scopes ?? []) {
        const customerId = scope.customer?.id;
        const sku = scope.product?.sku;
        if (customerId && sku) {
          if (!customerSkusMap.has(customerId)) {
            customerSkusMap.set(customerId, new Set());
          }
          customerSkusMap.get(customerId)!.add(sku);
        }
      }
    }

    return Array.from(customerSkusMap.entries()).map(([customerId, skus]) => ({
      customerId,
      skus: Array.from(skus),
    }));
  }, [pageDiscounts]);

  // Fetch list prices for all customers on the current page in parallel
  const { listPrices, isLoading: isListPricesLoading } =
    useMultiCustomerListPrices(customerInputs);

  // Handle delete click
  const handleDeleteClick = useCallback((discount: DiscountRow) => {
    setDiscountToDelete(discount);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!discountToDelete) return;
    try {
      await deleteDiscount.mutateAsync({ id: discountToDelete.id });
      setDiscountToDelete(null);
    } catch (error) {
      console.error('Failed to delete discount:', error);
    }
  }, [discountToDelete, deleteDiscount]);

  // Create columns based on selected tab
  const columns = useMemo(() => {
    switch (selectedTab) {
      case 'sku':
        return createSkuColumns(
          listPrices,
          isListPricesLoading,
          handleDeleteClick,
        );
      case 'category':
        return createCategoryColumns(handleDeleteClick);
      case 'line':
        return createLineColumns(handleDeleteClick);
      case 'group':
        return createGroupColumns(handleDeleteClick);
      case 'all_products':
        return createAllProductsColumns(handleDeleteClick);
      default:
        return createSkuColumns(
          listPrices,
          isListPricesLoading,
          handleDeleteClick,
        );
    }
  }, [selectedTab, listPrices, isListPricesLoading, handleDeleteClick]);

  const table = useReactTable({
    columns,
    data: tabFilteredDiscounts,
    state: {
      pagination: paginationState,
    },
    onPaginationChange: setPaginationState,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const handleAddSku = useCallback((sku: string) => {
    setSelectedSkus((prev) => [...prev, sku]);
    setSkuSearchOpen(false);
    setSkuSearchValue('');
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleRemoveSku = useCallback((sku: string) => {
    setSelectedSkus((prev) => prev.filter((s) => s !== sku));
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleAddCustomer = useCallback(
    (customer: { id: string; name: string }) => {
      setSelectedCustomers((prev) => [...prev, customer]);
      setCustomerSearchOpen(false);
      setCustomerSearchValue('');
      setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [],
  );

  const handleRemoveCustomer = useCallback((customerId: string) => {
    setSelectedCustomers((prev) => prev.filter((c) => c.id !== customerId));
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleAddCategory = useCallback((category: string) => {
    setSelectedCategories((prev) => [...prev, category]);
    setCategorySearchOpen(false);
    setCategorySearchValue('');
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleRemoveCategory = useCallback((category: string) => {
    setSelectedCategories((prev) => prev.filter((c) => c !== category));
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleAddProductLine = useCallback((productLine: string) => {
    setSelectedProductLines((prev) => [...prev, productLine]);
    setProductLineSearchOpen(false);
    setProductLineSearchValue('');
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleRemoveProductLine = useCallback((productLine: string) => {
    setSelectedProductLines((prev) => prev.filter((p) => p !== productLine));
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedSkus([]);
    setSelectedCustomers([]);
    setSelectedCategories([]);
    setSelectedProductLines([]);
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // SKUs available for selection (exclude already selected)
  const selectableSkus = useMemo(() => {
    return availableSkus.filter((sku) => !selectedSkus.includes(sku));
  }, [availableSkus, selectedSkus]);

  // Filter selectable SKUs by search value
  const filteredSelectableSkus = useMemo(() => {
    if (!skuSearchValue) return selectableSkus;
    const lower = skuSearchValue.toLowerCase();
    return selectableSkus.filter((sku) => sku.toLowerCase().includes(lower));
  }, [selectableSkus, skuSearchValue]);

  // Customers available for selection (exclude already selected)
  const selectableCustomers = useMemo(() => {
    const selectedIds = new Set(selectedCustomers.map((c) => c.id));
    return availableCustomers.filter((c) => !selectedIds.has(c.id));
  }, [availableCustomers, selectedCustomers]);

  // Filter selectable customers by search value
  const filteredSelectableCustomers = useMemo(() => {
    if (!customerSearchValue) return selectableCustomers;
    const lower = customerSearchValue.toLowerCase();
    return selectableCustomers.filter((c) =>
      c.name.toLowerCase().includes(lower),
    );
  }, [selectableCustomers, customerSearchValue]);

  // Categories available for selection (exclude already selected)
  const selectableCategories = useMemo(() => {
    return availableCategories.filter((c) => !selectedCategories.includes(c));
  }, [availableCategories, selectedCategories]);

  // Filter selectable categories by search value
  const filteredSelectableCategories = useMemo(() => {
    if (!categorySearchValue) return selectableCategories;
    const lower = categorySearchValue.toLowerCase();
    return selectableCategories.filter((c) => c.toLowerCase().includes(lower));
  }, [selectableCategories, categorySearchValue]);

  // Product lines available for selection (exclude already selected)
  const selectableProductLines = useMemo(() => {
    return availableProductLines.filter(
      (p) => !selectedProductLines.includes(p),
    );
  }, [availableProductLines, selectedProductLines]);

  // Filter selectable product lines by search value
  const filteredSelectableProductLines = useMemo(() => {
    if (!productLineSearchValue) return selectableProductLines;
    const lower = productLineSearchValue.toLowerCase();
    return selectableProductLines.filter((p) =>
      p.toLowerCase().includes(lower),
    );
  }, [selectableProductLines, productLineSearchValue]);

  const isFiltered =
    (visibleFilters.sku && selectedSkus.length > 0) ||
    (visibleFilters.customer && selectedCustomers.length > 0) ||
    (visibleFilters.category && selectedCategories.length > 0) ||
    (visibleFilters.productLine && selectedProductLines.length > 0);

  return (
    <Page title="Discounts">
      <div className="space-y-4">
        {/* Scope Type Tabs */}
        <Tabs value={selectedTab} onValueChange={handleTabChange}>
          <TabsList>
            {SCOPE_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
                <span className="ml-1.5 text-xs text-muted-foreground">
                  ({tabCounts[tab.value]})
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <Card className="p-4">
          {/* Top row: Filter buttons and Create button */}
          <div className="flex flex-wrap items-center gap-2">
            {/* SKU typeahead */}
            {visibleFilters.sku && (
              <Popover open={skuSearchOpen} onOpenChange={setSkuSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 border-dashed"
                  >
                    <PlusIcon className="h-3 w-3" />
                    <span>Add SKU filter</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search SKUs..."
                      value={skuSearchValue}
                      onValueChange={setSkuSearchValue}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {selectableSkus.length === 0
                          ? 'No SKUs available'
                          : 'No matching SKUs'}
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredSelectableSkus.slice(0, 50).map((sku) => (
                          <CommandItem
                            key={sku}
                            value={sku}
                            onSelect={() => handleAddSku(sku)}
                            className="font-mono text-sm cursor-pointer"
                          >
                            {sku}
                          </CommandItem>
                        ))}
                        {filteredSelectableSkus.length > 50 && (
                          <div className="px-2 py-1.5 text-xs text-muted-foreground">
                            +{filteredSelectableSkus.length - 50} more...
                          </div>
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}

            {/* Customer typeahead */}
            {visibleFilters.customer && (
              <Popover
                open={customerSearchOpen}
                onOpenChange={setCustomerSearchOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 border-dashed"
                  >
                    <PlusIcon className="h-3 w-3" />
                    <span>Add Customer filter</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search customers..."
                      value={customerSearchValue}
                      onValueChange={setCustomerSearchValue}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {selectableCustomers.length === 0
                          ? 'No customers available'
                          : 'No matching customers'}
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredSelectableCustomers
                          .slice(0, 50)
                          .map((customer) => (
                            <CommandItem
                              key={customer.id}
                              value={customer.id}
                              onSelect={() => handleAddCustomer(customer)}
                              className="text-sm cursor-pointer"
                            >
                              {customer.name}
                            </CommandItem>
                          ))}
                        {filteredSelectableCustomers.length > 50 && (
                          <div className="px-2 py-1.5 text-xs text-muted-foreground">
                            +{filteredSelectableCustomers.length - 50} more...
                          </div>
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}

            {/* Product Category typeahead */}
            {visibleFilters.category && (
              <Popover
                open={categorySearchOpen}
                onOpenChange={setCategorySearchOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 border-dashed"
                  >
                    <PlusIcon className="h-3 w-3" />
                    <span>Add Category filter</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search categories..."
                      value={categorySearchValue}
                      onValueChange={setCategorySearchValue}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {selectableCategories.length === 0
                          ? 'No categories available'
                          : 'No matching categories'}
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredSelectableCategories
                          .slice(0, 50)
                          .map((category) => (
                            <CommandItem
                              key={category}
                              value={category}
                              onSelect={() => handleAddCategory(category)}
                              className="text-sm cursor-pointer"
                            >
                              {category}
                            </CommandItem>
                          ))}
                        {filteredSelectableCategories.length > 50 && (
                          <div className="px-2 py-1.5 text-xs text-muted-foreground">
                            +{filteredSelectableCategories.length - 50} more...
                          </div>
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}

            {/* Product Line typeahead */}
            {visibleFilters.productLine && (
              <Popover
                open={productLineSearchOpen}
                onOpenChange={setProductLineSearchOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1 border-dashed"
                  >
                    <PlusIcon className="h-3 w-3" />
                    <span>Add Product Line filter</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search product lines..."
                      value={productLineSearchValue}
                      onValueChange={setProductLineSearchValue}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {selectableProductLines.length === 0
                          ? 'No product lines available'
                          : 'No matching product lines'}
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredSelectableProductLines
                          .slice(0, 50)
                          .map((productLine) => (
                            <CommandItem
                              key={productLine}
                              value={productLine}
                              onSelect={() => handleAddProductLine(productLine)}
                              className="text-sm cursor-pointer"
                            >
                              {productLine}
                            </CommandItem>
                          ))}
                        {filteredSelectableProductLines.length > 50 && (
                          <div className="px-2 py-1.5 text-xs text-muted-foreground">
                            +{filteredSelectableProductLines.length - 50}{' '}
                            more...
                          </div>
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}

            {/* Clear filters button */}
            {isFiltered && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-muted-foreground"
                onClick={handleClearFilters}
              >
                Clear filters
                <XIcon className="ml-1 h-3 w-3" />
              </Button>
            )}

            {/* Spacer to push Create button to the right */}
            <div className="flex-1" />

            {/* Create Discount button */}
            <Button size="sm" onClick={() => setCreateModalOpen(true)}>
              <PlusIcon className="h-3 w-3 mr-1" />
              Create Discount
            </Button>
          </div>

          {/* Bottom row: Selected filter pills */}
          {isFiltered && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t pt-3">
              {/* SKU pills */}
              {visibleFilters.sku &&
                selectedSkus.map((sku) => (
                  <Badge
                    key={sku}
                    variant="secondary"
                    className="h-5 gap-1 px-1.5 font-mono text-xs"
                  >
                    {sku}
                    <XIcon
                      className="h-2.5 w-2.5 cursor-pointer hover:text-destructive"
                      onClick={() => handleRemoveSku(sku)}
                    />
                  </Badge>
                ))}
              {/* Customer pills */}
              {visibleFilters.customer &&
                selectedCustomers.map((customer) => (
                  <Badge
                    key={customer.id}
                    variant="secondary"
                    className="h-5 gap-1 px-1.5 text-xs"
                  >
                    {customer.name}
                    <XIcon
                      className="h-2.5 w-2.5 cursor-pointer hover:text-destructive"
                      onClick={() => handleRemoveCustomer(customer.id)}
                    />
                  </Badge>
                ))}
              {/* Category pills */}
              {visibleFilters.category &&
                selectedCategories.map((category) => (
                  <Badge
                    key={`cat-${category}`}
                    variant="secondary"
                    className="h-5 gap-1 px-1.5 text-xs"
                  >
                    {category}
                    <XIcon
                      className="h-2.5 w-2.5 cursor-pointer hover:text-destructive"
                      onClick={() => handleRemoveCategory(category)}
                    />
                  </Badge>
                ))}
              {/* Product Line pills */}
              {visibleFilters.productLine &&
                selectedProductLines.map((productLine) => (
                  <Badge
                    key={`pl-${productLine}`}
                    variant="secondary"
                    className="h-5 gap-1 px-1.5 text-xs"
                  >
                    {productLine}
                    <XIcon
                      className="h-2.5 w-2.5 cursor-pointer hover:text-destructive"
                      onClick={() => handleRemoveProductLine(productLine)}
                    />
                  </Badge>
                ))}
            </div>
          )}
        </Card>

        <Card className="ring-1 ring-border">
          <DataTable
            key={`table-${paginationState.pageIndex}-${paginationState.pageSize}-${tabFilteredDiscounts.length}-${listPrices.size}-${isListPricesLoading}-${selectedTab}`}
            table={table}
            isLoading={isPendingOrLoading}
            emptyMessage="No discounts found for this scope type."
          />
          {/* Only show pagination when we have data - avoids "Page 1 of 0" during loading */}
          {!isPendingOrLoading && tabFilteredDiscounts.length > 0 && (
            <DataTablePagination
              key={`${paginationState.pageIndex}-${paginationState.pageSize}-${tabFilteredDiscounts.length}`}
              table={table}
            />
          )}
        </Card>
      </div>

      <CreateDiscountFormModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!discountToDelete}
        onOpenChange={(open) => {
          if (!open) setDiscountToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Discount</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the discount &quot;
              {discountToDelete?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDiscountToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteDiscount.isPending}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {deleteDiscount.isPending ? (
                <CircleNotchIcon className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
